/**
 * product-form-new: add-to-cart form, updates cart drawer/sections.
 * Variant availability mirrors Liquid (warehouse by country, stock, continue_selling_when_oos, inventory_policy).
 * Re-applies button state after variant-selects' async renderProductInfo so our OOS logic wins over variant.available.
 */
if (!customElements.get('product-form-new')) {
  customElements.define(
    'product-form-new',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        const idInput = this.form?.querySelector('[name=id]');
        if (idInput) idInput.disabled = false;
        this.form?.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.body = document.body;
      }

      connectedCallback() {
        this.availabilityData = this.getAvailabilityData();
        if (!this.availabilityData) return;

        this.updateButton();
        this.watchVariantId();
        this.observeButton();
      }

      disconnectedCallback() {
        if (this.variantCheckInterval) clearInterval(this.variantCheckInterval);
        if (this.variantApplyTimeouts) this.variantApplyTimeouts.forEach((t) => clearTimeout(t));
        this.buttonObserver?.disconnect();
      }

      getAvailabilityData() {
        const scriptId = this.getAttribute('data-availability-script');
        const script = scriptId ? document.getElementById(scriptId) : null;
        if (!script?.textContent) return null;
        try {
          return JSON.parse(script.textContent);
        } catch {
          return null;
        }
      }

      /**
       * Availability only from location stock (no variant.available): warehouse from country;
       * find matching stock entry; available if (qty > 0 or continue_selling_when_oos) and inventory_policy != 'deny'.
       * No matching location for warehouse → unavailable.
       */
      isVariantAvailable(variantId) {
        let defaultAvailability = this.availabilityData.variants.find((item) => Number(item.id) === Number(variantId)).available;
        const data = this.availabilityData;
        if (!data?.variants) return defaultAvailability;

        if(data.is_bundle_product) {
          //For now we only support bundle products with single variant
          return data.variant_available_single;
        }

        const id = Number(variantId);
        const v = data.variants.find((item) => Number(item.id) === id);
        if (!v) return defaultAvailability;

        if(v.requires_shipping == false) {
          return defaultAvailability;
        }

        const country = (data.country || '').toString();
        const useCanada = country === 'Canada' && !v.disable_canada;
        const warehouse = useCanada ? 'Canada' : 'USA';
        const continueSellingWhenOos = useCanada
          ? !!v.continue_selling_when_oos_canada
          : !!v.continue_selling_when_oos_international;

        const stock = Array.isArray(v.stock) ? v.stock : [];
        for (const loc of stock) {
          const locationName = (loc.location || '').toString();

          if (!locationName.includes(warehouse)) continue;

            const availableInLocation = Number(loc.available) || 0;
            const isVariantContinueSellingWhenOos = continueSellingWhenOos === true && v.inventory_policy == 'continue';
         
          if(availableInLocation <= 0 && isVariantContinueSellingWhenOos != true) {
            return false;
          }
          return defaultAvailability;
        }

        return defaultAvailability;
      }

      /** Re-query form/button/label so we always target live DOM (variant-selects overwrites button after async fetch). */
      updateButton() {
        const form = this.querySelector('form');
        const idInput = form?.querySelector('[name=id]');
        const variantId = idInput?.value ? parseInt(idInput.value, 10) : null;
        if (variantId == null || !Number.isFinite(variantId)) return;

        const submitButton = this.querySelector('[type="submit"]');
        const labelEl = this.querySelector('.product-form__submit-label');
        if (!submitButton) return;

        const available = this.isVariantAvailable(variantId);
        const addLabel = submitButton.getAttribute('data-add-label') || window.theme.variantStrings.addToCart;
        const soldOutLabel = submitButton.getAttribute('data-sold-out-label') || window.theme.variantStrings.soldOut;

        submitButton.disabled = !available;
        submitButton.classList.toggle('sold-out', !available);
        if (labelEl) labelEl.textContent = available ? addLabel : soldOutLabel;
      }

      /** Run update now; one delayed fallback in case observer misses (e.g. node replaced). */
      scheduleButtonUpdate() {
        this.updateButton();
        if (this.variantApplyTimeouts) this.variantApplyTimeouts.forEach((t) => clearTimeout(t));
        this.variantApplyTimeouts = [setTimeout(() => this.updateButton(), 400)];
      }

      /** Re-apply our state when something else (e.g. variant-selects) changes the button. */
      observeButton() {
        const submitButton = this.querySelector('[type="submit"]');
        if (!submitButton) return;

        this.buttonObserver = new MutationObserver(() => {
          const form = this.querySelector('form');
          const idInput = form?.querySelector('[name=id]');
          const variantId = idInput?.value ? parseInt(idInput.value, 10) : null;
          if (variantId == null || !Number.isFinite(variantId)) return;

          const btn = this.querySelector('[type="submit"]');
          const labelEl = this.querySelector('.product-form__submit-label');
          if (!btn) return;

          const expectedAvailable = this.isVariantAvailable(variantId);
          const addLabel = btn.getAttribute('data-add-label') || window.theme.variantStrings.addToCart;
          const soldOutLabel = btn.getAttribute('data-sold-out-label') || window.theme.variantStrings.soldOut;
          const expectedDisabled = !expectedAvailable;
          const expectedText = expectedAvailable ? addLabel : soldOutLabel;
          const currentDisabled = btn.disabled;
          const currentText = (labelEl?.textContent ?? '').trim();

          if (currentDisabled !== expectedDisabled || currentText !== expectedText) {
            this.updateButton();
          }
        });

        this.buttonObserver.observe(submitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      watchVariantId() {
        const form = this.querySelector('form');
        const idInput = form?.querySelector('[name=id]');
        if (!idInput) return;

        let lastId = idInput.value;
        const apply = () => {
          if (idInput.value === lastId) return;
          lastId = idInput.value;
          this.scheduleButtonUpdate();
        };

        idInput.addEventListener('change', apply);

        this.variantCheckInterval = setInterval(apply, 350);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitButton = this.querySelector('[type="submit"]');
        if (submitButton.classList.contains('loading')) return;

        this.handleErrorMessage();
        submitButton.setAttribute('aria-disabled', true);
        submitButton.classList.add('loading');

        const formData = new FormData(this.form);
        formData.append('sections', this.getSectionsToRender().map((s) => s.section));
        formData.append('sections_url', window.location.pathname);

        fetch(`${theme.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript',
          },
          body: formData,
        })
          .then((res) => res.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);
              return;
            }
            this.renderContents(response);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            submitButton.classList.remove('loading');
            submitButton.removeAttribute('aria-disabled');
          });
      }

      getSectionsToRender() {
        return [
          { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
          { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
          { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' },
        ];
      }

      renderContents(parsedState) {
        if (!parsedState?.sections) return;

        this.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) return;

          const html = parsedState.sections[section.section];
          if (!html) return;

          const el = container.querySelector(section.selector) || container;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const inner = parsed.querySelector(section.selector) || parsed.body.firstElementChild;
          el.innerHTML = inner ? inner.innerHTML : html;

          const quantities = el.querySelectorAll('.quantity:not(.buttons_added)');
          quantities.forEach((q) => new QuantityInput(q));

          if (typeof CartDrawer !== 'undefined') new CartDrawer();
          if (typeof Cart !== 'undefined') new Cart().renderContents(parsedState);
        });

        const drawer = document.getElementById('Cart-Drawer');
        if (drawer) {
          this.body.classList.add('open-cc');
          drawer.classList.add('active');
        }

        const productDrawer = document.getElementById('Product-Drawer');
        if (productDrawer?.contains(this)) {
          productDrawer.classList.remove('active');
          if (!document.getElementById('Cart-Drawer')) this.body.classList.remove('open-cc');
        }
      }

      handleErrorMessage(errorMessage = false) {
        const wrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        const msg = this.errorMessage || wrapper?.querySelector('.product-form__error-message');
        this.errorMessageWrapper = wrapper;
        this.errorMessage = msg;

        if (wrapper) wrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage && msg) msg.textContent = errorMessage;
      }
    }
  );
}
